import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { SortOrder } from '../../shared/enums/order';
import { RootState } from '../../store/root-reducer';
import toaster from '../../shared/toaster';
import { Routes } from '../../shared/routes';
import CsvEnrichment from './csv-enrichment';
import NewEnrichmentPage from './components/new-enrichment-modal/new-enrichment-page';
import EnrichmentStatusPage from './components/enrichment-status/enrichment-status-page';
import {
  setDateRange,
  setSearchQuery,
  setStatusFilter,
  setTypeFilter,
  setPage,
  setPageSize,
  setSort,
  resetWizard,
  clearLastCreatedJobId,
  resetRecords,
  resetJobsRequestStatus,
} from './csv-enrichment-slice';
import {
  getEnrichmentJobs,
  getEnrichmentStats,
  deleteEnrichmentJob,
  // getEnrichmentJobStatus,
  getEnrichmentJobRecords,
  loadMoreEnrichmentJobRecords,
} from './extra-actions';
import {
  exportEnrichmentJobApi,
  failedExportJobApi,
  addJobToProspectsApi,
  addJobToSequenceApi,
  cancelEnrichmentJobApi,
} from './helpers/csv-enrichment.api';
import { getEnrichmentApiSuccessMessage } from './helpers/csv-enrichment.helpers';
import { enableBulkActionRequestPolling } from '../home/home-slice';
import ConfirmationModalV3 from '../../shared/design-system/components/atoms/confirmation-modal/confirmation-modal-v3';
import {
  DateRangeFilter,
  EnrichmentStatus,
  EnrichmentType,
} from './enums/csv-enrichment.enums';
import { RequestStatus } from '../../shared/enums/request-status';
import AddToSequenceModalContainer from '../prospect/components/prospect-list/components/modals/add-to-sequence-modal/add-to-sequence-modal-container';
import { ConfirmationModalIconType } from '../../shared/design-system/components/atoms/confirmation-modal/enum';
import { executeOnRequestStatusWithPrevStatusCheck } from '../../shared/utils/execute-on-request-status';

const BACKOFF_DELAYS = [1000, 2000, 4000, 8000, 16000];
const MAX_POLL_COUNT = 50;
const VALID_PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

// Extract the job ID segment from a status page URL
// e.g. /v2/leads/csv-enrichment/status/507f1f77bcf86cd799439011 → "507f..."
const extractStatusJobId = (pathname: string): string | null => {
  const parts = pathname.split('/');
  const statusIdx = parts.indexOf('status');
  const candidate = statusIdx !== -1 ? parts[statusIdx + 1] : null;
  if (candidate && /^[a-f\d]{24}$/i.test(candidate)) {
    return candidate;
  }
  return null;
};

const CsvEnrichmentContainer: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const statusPollingRef = useRef<NodeJS.Timeout | null>(null);
  const statusPollCountRef = useRef(0);
  const prevIsNonListRef = useRef(
    location.pathname.includes('/csv-enrichment/status') ||
      location.pathname.includes('/csv-enrichment/new'),
  );

  const isNewEnrichmentPage = location.pathname.includes('/csv-enrichment/new');
  const isStatusPage = location.pathname.includes('/csv-enrichment/status');
  const statusPageJobId = isStatusPage
    ? extractStatusJobId(location.pathname)
    : null;

  const {
    jobs,
    jobsRequestStatus,
    stats,
    statsRequestStatus,
    dateRange,
    searchQuery,
    statusFilter,
    typeFilter,
    page,
    pageSize,
    totalJobs,
    sortBy,
    sortOrder,
    pollingJobIds,
    createJobRequestStatus,
    lastCreatedJobId,
    shouldPollStatusPage,
    records,
    recordColumns,
    recordsRequestStatus,
    recordsJob,
    recordsHasMore,
    recordsIsLoadingMore,
  } = useSelector((state: RootState) => state.csvEnrichment);

  // Derive current status page job and whether it's in a terminal state
  // const statusJob = statusPageJobId
  //   ? jobs.find((j) => j.id === statusPageJobId) || null
  //   : null;
  const isJobTerminal =
    recordsJob != null &&
    (recordsJob.status === EnrichmentStatus.COMPLETED ||
      recordsJob.status === EnrichmentStatus.FAILED ||
      (recordsJob.status === EnrichmentStatus.CANCELLED &&
        !records.some((r) => r.isRevealing === true)));

  const [sequenceModalJobId, setSequenceModalJobId] = useState<string | null>(
    null,
  );
  const [cancelJobId, setCancelJobId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [deleteJobId, setDeleteJobId] = useState<string | null>(null);
  const [isDeletingJob, setIsDeletingJob] = useState(false);

  const [isInitialized, setIsInitialized] = useState(false);
  const pollCountRef = useRef(0);
  const pollingJobIdsRef = useRef(pollingJobIds);
  const jobsRequestStatusRef = useRef(jobsRequestStatus);
  const recordsRequestStatusRef = useRef(recordsRequestStatus);

  const prevCreateStatusRef = useRef(createJobRequestStatus);
  const prevRecordsStatusRef = useRef(recordsRequestStatus);

  // Dashboard list + stats only — not on job status or new-enrichment sub-routes
  const isDashboardRoute = !isNewEnrichmentPage && !isStatusPage;

  // Jobs — re-fetch on mount and whenever filters/pagination change
  // Reads page/pageSize/status/type/search directly from the URL so the payload
  // always reflects the current URL regardless of Redux state.
  const fetchData = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const urlStatus = params.get('status') as EnrichmentStatus | null;
    const urlType = params.get('type') as EnrichmentType | null;
    const urlSearch = params.get('search') || '';
    const urlPage = parseInt(params.get('page') || '1', 10);
    const urlPageSizeRaw = parseInt(
      params.get('pageSize') || String(DEFAULT_PAGE_SIZE),
      10,
    );
    const urlPageSize = VALID_PAGE_SIZES.includes(urlPageSizeRaw)
      ? urlPageSizeRaw
      : DEFAULT_PAGE_SIZE;

    dispatch(
      getEnrichmentJobs({
        dateRange,
        search: urlSearch,
        status: urlStatus,
        type: urlType,
        page: urlPage > 0 ? urlPage : 1,
        pageSize: urlPageSize,
        sortBy,
        sortOrder,
      }),
    );
  }, [dispatch, location.search, dateRange, sortBy, sortOrder]);

  useEffect(() => {
    if (!isDashboardRoute || !isInitialized) {
      return;
    }
    if (prevIsNonListRef.current) {
      return;
    }
    dispatch(resetJobsRequestStatus());
    fetchData();
  }, [fetchData, isDashboardRoute, dispatch, isInitialized]);

  // Stats — once per dashboard visit (not on pagination/filter changes)
  // useEffect(() => {
  //   if (!isDashboardRoute) return;
  //   dispatch(getEnrichmentStats());
  // }, [isDashboardRoute, dispatch]);

  // Navigate to status page on successful job creation
  useEffect(() => {
    if (
      prevCreateStatusRef.current === RequestStatus.Pending &&
      createJobRequestStatus === RequestStatus.Succeeded &&
      lastCreatedJobId
    ) {
      toaster.success('Enrichment job started');
      if (shouldPollStatusPage === true) {
        dispatch(enableBulkActionRequestPolling());
      }
      history.push(`${Routes.CSV_Enrichment_Status}/${lastCreatedJobId}`);
      dispatch(clearLastCreatedJobId());
    }
    prevCreateStatusRef.current = createJobRequestStatus;
  }, [
    createJobRequestStatus,
    history,
    lastCreatedJobId,
    shouldPollStatusPage,
    dispatch,
  ]);

  // Keep refs in sync so timeout/interval callbacks always see the latest value
  useEffect(() => {
    pollingJobIdsRef.current = pollingJobIds;
  }, [pollingJobIds]);

  useEffect(() => {
    jobsRequestStatusRef.current = jobsRequestStatus;
  }, [jobsRequestStatus]);

  useEffect(() => {
    recordsRequestStatusRef.current = recordsRequestStatus;
  }, [recordsRequestStatus]);

  useEffect(() => {
    executeOnRequestStatusWithPrevStatusCheck({
      status: recordsRequestStatus,
      prevStatus: prevRecordsStatusRef?.current,
      onFailed: () => {
        if (isStatusPage) {
          history.push(Routes.CSV_Enrichment);
        }
      },
    });
    prevRecordsStatusRef.current = recordsRequestStatus;
  }, [recordsRequestStatus, isStatusPage, history]);

  // Polling for active jobs (main dashboard only) — exponential backoff, max 50 calls
  const hasPollingJobs = pollingJobIds.length > 0;
  const shouldPollDashboardJobs = hasPollingJobs && isDashboardRoute;
  useEffect(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }

    if (!shouldPollDashboardJobs) {
      pollCountRef.current = 0;
      return undefined;
    }

    pollCountRef.current = 0; // fresh cycle each time jobs become active

    const schedule = () => {
      if (
        !pollingJobIdsRef.current.length ||
        pollCountRef.current >= MAX_POLL_COUNT ||
        jobsRequestStatusRef.current === RequestStatus.Failed
      ) {
        pollCountRef.current = 0;
        return;
      }
      const delay =
        BACKOFF_DELAYS[pollCountRef.current % BACKOFF_DELAYS.length];
      pollingRef.current = setTimeout(() => {
        pollCountRef.current += 1;
        fetchData();
        schedule();
      }, delay);
    };

    schedule();

    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [shouldPollDashboardJobs, fetchData]);

  // Reset stale records whenever we navigate to a different status page
  useEffect(() => {
    if (statusPageJobId) {
      dispatch(resetRecords());
    }
  }, [dispatch, statusPageJobId]);

  // Status page: initial records fetch — once per job page
  useEffect(() => {
    if (!statusPageJobId) return;
    statusPollCountRef.current = 0;
    dispatch(getEnrichmentJobRecords(statusPageJobId));
  }, [dispatch, statusPageJobId]);

  // Status page: manage polling interval — start when active, stop when terminal
  useEffect(() => {
    if (!statusPageJobId || isJobTerminal) {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
        statusPollingRef.current = null;
      }
      return;
    }

    statusPollingRef.current = setInterval(() => {
      if (statusPollCountRef.current >= 50) {
        return;
      }
      if (recordsRequestStatusRef.current === RequestStatus.Failed) {
        clearInterval(statusPollingRef.current!);
        statusPollingRef.current = null;
        return;
      }
      statusPollCountRef.current += 1;
      dispatch(
        getEnrichmentJobRecords({
          jobId: statusPageJobId,
          preserveAccumulatedPages: true,
        }),
      );
    }, 5000);

    // eslint-disable-next-line consistent-return
    return () => {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
        statusPollingRef.current = null;
      }
    };
  }, [dispatch, statusPageJobId, isJobTerminal, shouldPollStatusPage]);

  const buildParams = (
    overrides: {
      status?: EnrichmentStatus | null;
      type?: EnrichmentType | null;
      search?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ): string => {
    const s = overrides.status !== undefined ? overrides.status : statusFilter;
    const t = overrides.type !== undefined ? overrides.type : typeFilter;
    const q = overrides.search !== undefined ? overrides.search : searchQuery;
    const p = overrides.page !== undefined ? overrides.page : page;
    const ps = overrides.pageSize !== undefined ? overrides.pageSize : pageSize;
    const params = new URLSearchParams();
    if (s) params.set('status', s);
    if (t) params.set('type', t);
    if (q) params.set('search', q);
    params.set('page', String(p));
    params.set('pageSize', String(ps));
    return params.toString();
  };

  // Initialize state from URL query params on mount, then normalize the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status') as EnrichmentStatus | null;
    const type = params.get('type') as EnrichmentType | null;
    const search = params.get('search') || '';
    const pageParam = parseInt(params.get('page') || '1', 10);
    const pageSizeParam = parseInt(
      params.get('pageSize') || String(DEFAULT_PAGE_SIZE),
      10,
    );
    const validPageSize = VALID_PAGE_SIZES.includes(pageSizeParam)
      ? pageSizeParam
      : DEFAULT_PAGE_SIZE;

    batch(() => {
      dispatch(setStatusFilter(status));
      dispatch(setTypeFilter(type));
      dispatch(setSearchQuery(search));
      dispatch(setPageSize(validPageSize));
      dispatch(setPage(pageParam > 0 ? pageParam : 1));
      setIsInitialized(true);
    });

    // Always write normalized URL (adds defaults, corrects invalid pageSize)
    const normalized = new URLSearchParams();
    if (status) normalized.set('status', status);
    if (type) normalized.set('type', type);
    if (search) normalized.set('search', search);
    normalized.set('page', String(pageParam > 0 ? pageParam : 1));
    normalized.set('pageSize', String(validPageSize));
    const normalizedSearch = normalized.toString();
    if (location.pathname.startsWith('/v2/leads/csv-enrichment')) {
      history.replace({
        search: normalizedSearch ? `?${normalizedSearch}` : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep Redux pagination state in sync with URL (hard refresh / manual URL edits)
  useEffect(() => {
    if (!isDashboardRoute || !isInitialized) return;

    const params = new URLSearchParams(location.search);
    const urlPageRaw = parseInt(params.get('page') || '1', 10);
    const urlPage = urlPageRaw > 0 ? urlPageRaw : 1;
    const urlPageSizeRaw = parseInt(
      params.get('pageSize') || String(DEFAULT_PAGE_SIZE),
      10,
    );
    const urlPageSize = VALID_PAGE_SIZES.includes(urlPageSizeRaw)
      ? urlPageSizeRaw
      : DEFAULT_PAGE_SIZE;

    if (urlPage === page && urlPageSize === pageSize) return;

    batch(() => {
      // setPageSize resets page to 1 in reducer, so always set pageSize first.
      if (urlPageSize !== pageSize) {
        dispatch(setPageSize(urlPageSize));
      }
      if (urlPage !== page) {
        dispatch(setPage(urlPage));
      }
    });
  }, [
    dispatch,
    isDashboardRoute,
    isInitialized,
    location.search,
    page,
    pageSize,
  ]);

  // Re-sync URL when returning to list view from status/new page
  useEffect(() => {
    const isNonList = isStatusPage || isNewEnrichmentPage;
    const wasNonList = prevIsNonListRef.current;
    prevIsNonListRef.current = isNonList;

    if (wasNonList && !isNonList) {
      history.replace({ search: buildParams() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isStatusPage,
    isNewEnrichmentPage,
    statusFilter,
    typeFilter,
    searchQuery,
    page,
    pageSize,
  ]);

  const handleDateRangeChange = (range: DateRangeFilter) => {
    dispatch(setDateRange(range));
  };

  const handleSearchChange = (value: string) => {
    dispatch(resetJobsRequestStatus());
    dispatch(setSearchQuery(value));
    const params = new URLSearchParams(window.location.search);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    history.replace({ search: params.toString() });
  };

  const handleStatusFilterChange = (status: EnrichmentStatus | null) => {
    dispatch(resetJobsRequestStatus());
    dispatch(setStatusFilter(status));
    history.replace({ search: buildParams({ status, page: 1 }) });
  };

  const handleTypeFilterChange = (type: EnrichmentType | null) => {
    dispatch(resetJobsRequestStatus());
    dispatch(setTypeFilter(type));
    history.replace({ search: buildParams({ type, page: 1 }) });
  };

  const handleClearAllFilters = () => {
    dispatch(resetJobsRequestStatus());
    dispatch(setStatusFilter(null));
    dispatch(setTypeFilter(null));
    history.replace({
      search: buildParams({ status: null, type: null, page: 1 }),
    });
  };

  const handleNewEnrichment = () => {
    dispatch(resetWizard());
    history.push(Routes.CSV_Enrichment_New);
  };

  const handleAddToProspects = async (id: string) => {
    try {
      const response = await addJobToProspectsApi(id);
      toaster.success(
        getEnrichmentApiSuccessMessage(response, 'Contacts added to prospects'),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddToSequence = (id: string) => {
    setSequenceModalJobId(id);
  };

  const handleSequenceSubmit = async (sequenceId: number, stepId: number) => {
    if (!sequenceModalJobId) return;
    const jobId = sequenceModalJobId;
    setSequenceModalJobId(null);
    try {
      const response = await addJobToSequenceApi(jobId, sequenceId, stepId);
      toaster.success(
        getEnrichmentApiSuccessMessage(response, 'Contacts added to sequence'),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleFailedReport = async (id: string) => {
    try {
      const response = await failedExportJobApi(id);
      toaster.success(
        getEnrichmentApiSuccessMessage(
          response,
          'Failed report exported successfully',
        ),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleExport = async (id: string) => {
    try {
      const response = await exportEnrichmentJobApi(id);
      toaster.success(
        getEnrichmentApiSuccessMessage(response, 'CSV exported successfully'),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenDeleteModal = (id: string) => setDeleteJobId(id);

  const handleDeleteConfirm = async () => {
    if (!deleteJobId) return;
    setIsDeletingJob(true);
    try {
      const action = await dispatch(deleteEnrichmentJob(deleteJobId) as any);
      if (deleteEnrichmentJob.fulfilled.match(action)) {
        toaster.success(action.payload?.message);
        setDeleteJobId(null);
      } else {
        throw new Error('deleteEnrichmentJob failed');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeletingJob(false);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (statusPageJobId) {
      dispatch(loadMoreEnrichmentJobRecords(statusPageJobId));
    }
  }, [dispatch, statusPageJobId]);

  const handleOpenCancelModal = (id: string) => setCancelJobId(id);

  const handleCancelConfirm = async () => {
    if (!cancelJobId) return;
    setIsCancelling(true);
    try {
      const response = await cancelEnrichmentJobApi(cancelJobId);
      toaster.success(
        getEnrichmentApiSuccessMessage(response, 'Job cancelled'),
      );
      setCancelJobId(null);
      if (isStatusPage && statusPageJobId) {
        // dispatch(getEnrichmentJobStatus(statusPageJobId));
        dispatch(getEnrichmentJobRecords(statusPageJobId));
      } else {
        fetchData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSortChange = (newSortBy: string, order: SortOrder) => {
    dispatch(setSort({ sortBy: newSortBy, sortOrder: order }));
  };

  const handlePaginationChange = (payload: {
    page?: number;
    limit?: number;
  }) => {
    if (payload.limit && payload.limit !== pageSize) {
      dispatch(setPageSize(payload.limit));
      history.replace({
        search: buildParams({ pageSize: payload.limit, page: 1 }),
      });
      return;
    }
    if (payload.page && payload.page !== page) {
      dispatch(setPage(payload.page));
      history.replace({ search: buildParams({ page: payload.page }) });
    }
  };

  const handleViewJob = (id: string) => {
    history.push(`${Routes.CSV_Enrichment_Status}/${id}`);
  };

  return (
    <>
      {isNewEnrichmentPage && <NewEnrichmentPage />}
      {isStatusPage && (
        <EnrichmentStatusPage
          job={recordsJob}
          recordsJob={recordsJob}
          records={records}
          recordColumns={recordColumns}
          recordsRequestStatus={recordsRequestStatus}
          recordsHasMore={recordsHasMore}
          recordsIsLoadingMore={recordsIsLoadingMore}
          onLoadMore={handleLoadMore}
          onExport={handleExport}
          onAddToProspects={handleAddToProspects}
          onAddToSequence={handleAddToSequence}
          failedReport={handleFailedReport}
          onCancel={() => handleOpenCancelModal(recordsJob?.jobId ?? '')}
        />
      )}
      {!isNewEnrichmentPage && !isStatusPage && (
        <CsvEnrichment
          jobs={jobs}
          jobsRequestStatus={jobsRequestStatus}
          stats={stats}
          statsRequestStatus={statsRequestStatus}
          dateRange={dateRange}
          searchQuery={searchQuery}
          statusFilter={statusFilter}
          typeFilter={typeFilter}
          page={page}
          pageSize={pageSize}
          totalJobs={totalJobs}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          onDateRangeChange={handleDateRangeChange}
          onSearchChange={handleSearchChange}
          onStatusFilterChange={handleStatusFilterChange}
          onTypeFilterChange={handleTypeFilterChange}
          onClearAllFilters={handleClearAllFilters}
          onPaginationChange={handlePaginationChange}
          onNewEnrichment={handleNewEnrichment}
          onAddToProspects={handleAddToProspects}
          onAddToSequence={handleAddToSequence}
          onExport={handleExport}
          onFailedReport={handleFailedReport}
          onDelete={handleOpenDeleteModal}
          onCancel={handleOpenCancelModal}
          onRetry={fetchData}
          onViewJob={handleViewJob}
        />
      )}
      {sequenceModalJobId !== null && (
        <AddToSequenceModalContainer
          show
          onHide={() => setSequenceModalJobId(null)}
          addToSequence={handleSequenceSubmit}
        />
      )}
      <ConfirmationModalV3
        show={cancelJobId !== null}
        iconType={ConfirmationModalIconType.INFO_CIRCLE_RED}
        title="Cancel Enrichment"
        content="Are you sure you want to cancel this enrichment? You can still download partial enriched data from history."
        onClose={() => setCancelJobId(null)}
        onSubmit={handleCancelConfirm}
        submitButtonText="Cancel Enrichment"
        cancelButtonText="Keep Running"
        isSubmitLoading={isCancelling}
        isSubmitDisabled={isCancelling}
        className="bs-csv-enrichment__cancel-modal"
      />
      <ConfirmationModalV3
        show={deleteJobId !== null}
        iconType={ConfirmationModalIconType.INFO_CIRCLE_RED}
        title="Delete Enrichment Job"
        content="Are you sure you want to delete this enrichment job? This action cannot be undone."
        onClose={() => setDeleteJobId(null)}
        onSubmit={handleDeleteConfirm}
        submitButtonText="Delete"
        cancelButtonText="Cancel"
        isSubmitLoading={isDeletingJob}
        isSubmitDisabled={isDeletingJob}
        className="bs-csv-enrichment__delete-modal"
      />
    </>
  );
};

export default CsvEnrichmentContainer;
